import React, { useState, useEffect } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Autocomplete,
  Chip,
  TextField,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import {
  CountryCodeWithPhoneNumber,
  DescriptionInputComponent,
  TextInputComponent,
} from "../../../common/components";
import CloseIcon from "../../../assets/icons/closeIcon.svg";
import CreateLeadCapturePopupConfirmationQRCode  from "../../lead-capture/create-lead-capture-popups/create-lead-capture-popup-confirmation/create-lead-capture-popup-confirmation-qr-code";
import { shareLeadCaptureForms } from "../../../api/lead-capture";
import { shareLandingPage } from "../../../api/campaign";
import { handlePrint } from "../../surveys/qr-code/qr-code-list.constant";
import { emailValidationRegex } from "../../../constants/regexStrings";

const SaveAndSharePopup = (props: any) => {
  const {
    isShowConfirmationPopup,
    setIsShowConfirmationPopup,
    formLink,
    setAlertOpen,
    setAlertMessage,
    setAlertType,
    setLoading,
    qrCodeUrl,
    setCreateLeadCaptureInitialValues = () => {},
  } = props;

  const [emailsData, setEmailsDataData] = useState<any>([]);
  const validationSchema = () =>
    Yup.object().shape({
      emailAddress: Yup.array()
        .of(Yup.string().email())
        .when("selectedOption", (val: any) => {
          return val[0] === "emailAddress"
            ? Yup.array()
                .of(
                  Yup.string().email((dataArray: any) => {
                    return dataArray?.originalPath ===
                      `emailAddress[${emailsData?.length - 1}]`
                      ? "One or more email addresses are invalid. Please correct them before Sharing."
                      : "";
                  })
                )
                .min(1, "Email address is required")
                .max(10, "You can share with up to 10 email addresses only")
            : Yup.array().notRequired();
        }),
      emailBody: Yup.string().when("selectedOption", (val: any) => {
        return val[0] === "emailAddress"
          ? Yup.string().required("Email body is required")
          : Yup.string().notRequired();
      }),
      emailSubject: Yup.string().when("selectedOption", (val: any) => {
        return val[0] === "emailAddress"
          ? Yup.string().required("Email subject is required")
          : Yup.string().notRequired();
      }),
    });

  const handleSave = async (values: any, resetForm: any) => {
    setLoading(true);
    try {
      const payload = {
        emailIds: values?.emailAddress,
        body: values?.emailBody,
        subject: values?.emailSubject,
      };
      const response = await shareLandingPage(payload);
      setLoading(false);
      setIsShowConfirmationPopup(false);
      setAlertMessage(response?.data?.data?.message);
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setLoading(false);

      const errorData = error?.response?.data;

      let errorMessages: string[] = [];
      
      if (Array.isArray(errorData?.errors)) {
        errorMessages = errorData.errors.map((errObj: any) => {
          const raw = Object.values(errObj)[0]; // get the message from object
          let cleanedError = raw;
      
          if (typeof cleanedError === "string" && cleanedError.startsWith('"') && cleanedError.endsWith('"')) {
            cleanedError = cleanedError.slice(1, -1);
          }
      
          const match = typeof cleanedError === "string" 
            ? cleanedError.match(/emailIds\[(\d+)\]/) 
            : null;
          if (match) {
            const index = parseInt(match[1], 10) + 1;
            return `Email Id ${index} must be a valid email`;
          }
      
          return cleanedError;
        });
      } else if (typeof errorData?.message === "string") {
        errorMessages = [errorData.message];
      } else {
        errorMessages = ["Failed to share Landing Page"];
      }
      
      const userFriendlyError = errorMessages.join(", ");
      setAlertMessage(userFriendlyError);      setAlertType("error");
      setAlertOpen(true);
    }
  };

  return (
    <>
      <Dialog
        open={isShowConfirmationPopup}
        onClose={() => {
          setIsShowConfirmationPopup(false);
          setCreateLeadCaptureInitialValues(null);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={`create-lead-capture-popup-confirmation`}
      >
        <DialogTitle id="alert-dialog-title">
          <div className="header-wrapper" style={{ padding: "10px 0" }}>
            <div className="image-wrapper">
              <div
                className="popup-title"
                style={{ fontSize: "16px", fontWeight: "600" }}
              >
                Share your Landing Page
              </div>
              <img
                src={CloseIcon}
                className="close-icon"
                onClick={() => {
                  setIsShowConfirmationPopup(false);
                  setCreateLeadCaptureInitialValues(null);
                }}
              />
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="create-lead-capture-popup-confirmation-description">
            <Formik
              initialValues={{
                selectedOption: "emailAddress",
                emailAddress: [],
                sms: "",
                embedLink: formLink || "",
                id: "landing-page-form",
                showCopied: false,
                type: "email address",
                emailSubject: "",
                emailBody: "",
              }}
              validationSchema={validationSchema}
              validateOnChange={true}
              enableReinitialize={true}
              onSubmit={(values, { resetForm }) => {
                console.log("handle submit called", values);
                handleSave(values, resetForm);
              }}
            >
              {({
                values,
                setFieldValue,
                touched,
                setFieldTouched,
                handleSubmit,
                validateForm,
              }) => (
                <Form>
                  <div
                    className="cancel-popup-sub-text"
                    style={{ marginBottom: "10px" }}
                  >
                    You can now save the designed landing page form or share it
                    with your target audience via {values?.type}.
                  </div>
                  <div className="create-lead-capture-popup-confirmation-description-checkbox">
                    <FormControl>
                      <RadioGroup row>
                        <FormControlLabel
                          value="Email address"
                          control={
                            <Radio
                              onChange={() => {
                                setFieldValue("selectedOption", "emailAddress");
                                setFieldValue("sms", "");
                                setFieldValue("embedLink", "");
                                setFieldValue("emailAddress", []);
                                setFieldValue("showCopied", false);
                                setFieldValue("type", "email address");
                              }}
                              checked={
                                values?.selectedOption === "emailAddress"
                              }
                            />
                          }
                          label="Email address"
                          style={{ fontSize: "14px", fontWeight: "400" }}
                        />

                        <FormControlLabel
                          value="Embed Link"
                          control={
                            <Radio
                              onChange={() => {
                                setFieldValue("selectedOption", "embedLink");
                                setFieldValue("emailAddress", []);
                                setFieldValue("sms", "");
                                setFieldValue("embedLink", formLink || "");
                                setFieldValue("showCopied", false);
                                setFieldValue("type", "embed link ");
                              }}
                              checked={values?.selectedOption === "embedLink"}
                            />
                          }
                          label="Embed Link"
                          style={{ fontSize: "14px", fontWeight: "400" }}
                        />
                        <FormControlLabel
                          value="QR Code"
                          control={
                            <Radio
                              onChange={() => {
                                setFieldValue("selectedOption", "QR Code");
                                setFieldValue("emailAddress", []);
                                setFieldValue("sms", "");
                                setFieldValue("embedLink", formLink || "");
                                setFieldValue("showCopied", false);
                                setFieldValue("type", "QR Code ");
                              }}
                              checked={
                                values?.selectedOption === "QR Code"
                              }
                            />
                          }
                          label="QR Code"
                        />
                      </RadioGroup>                    
                    </FormControl>
                  </div>
                  <div className="create-lead-capture-popup-confirmation-description-checkbox-input">
                    {values?.selectedOption === "emailAddress" ? (
                      <div
                        className="form-group"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        <Autocomplete
                          multiple
                          id="tags-filled"
                          options={[]}
                          defaultValue={[]}
                          freeSolo
                          onChange={(e: any, value: any) => {
                            setEmailsDataData(value);
                            setTimeout(() => {
                              setFieldValue("emailAddress", value);
                            }, 500);
                          }}
                          renderTags={(value: any[], getTagProps: any) =>
                            value.map((option: any, index: any) => {
                              return (
                                <Chip
                                  key={index}
                                  label={option}
                                  color="primary"
                                  {...getTagProps({ index })}
                                />
                              );
                            })
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              placeholder="Add a receiver by pressing enter after its dotName or address"
                            />
                          )}
                        />
                        {touched?.emailAddress && (
                          <ErrorMessage
                            name={"emailAddress"}
                            component="div"
                            className="error-message"
                          />
                        )}
                        <div style={{ marginTop: "10px" }}>
                          <TextInputComponent
                            type="text"
                            fieldName="emailSubject"
                            placeholder="Enter Subject"
                            optional={true}
                            disabled={false}
                            value={values.emailSubject}
                            onChange={(e: any) =>
                              setFieldValue("emailSubject", e?.target?.value)
                            }
                          />
                        </div>
                        <div className="form-grid-three">
                          <DescriptionInputComponent
                            fieldName="emailBody"
                            placeholder="Body"
                            optional={false}
                            disabled={false}
                            value={values.emailBody}
                            onChange={(e: any) =>
                              setFieldValue("emailBody", e?.target?.value)
                            }
                          />
                        </div>
                      </div>
                    ) : values?.selectedOption === "sms" ? (
                      <div className="form-group">
                        <CountryCodeWithPhoneNumber
                          fieldName="sms"
                          placeholder={values?.sms ? "" : "Enter Phone Number"}
                          onChange={(
                            value: string,
                            country: any,
                            one: any,
                            two: any
                          ) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object"
                              );
                              return;
                            }

                            if (`+${countryCode}` == two) {
                              setFieldValue("sms", null);
                            } else {
                              if (two?.includes(`+${countryCode}`)) {
                                setFieldValue("sms", two);
                              } else {
                                setFieldValue("sms", countryCode);
                              }
                            }
                          }}
                          onBlur={() => {
                            setFieldTouched("sms", true);
                          }}
                          value={values?.sms || "+1"}
                          onKeyDown={(value: any) => {
                            if (
                              value.location == 0 &&
                              value.key == "Backspace"
                            ) {
                              setFieldValue(
                                "sms",
                                values?.sms?.substring(
                                  0,
                                  values?.sms?.length - 1
                                )
                              );
                            }
                            if (value.location == 0 && value.key == "Delete") {
                              setFieldValue(
                                "sms",
                                values?.sms?.substring(
                                  0,
                                  values?.sms?.length - 1
                                )
                              );
                            }
                          }}
                          defaultCountry="us"
                          optional={true}
                          // countryCodeEditable
                        />
                        {touched?.sms && touched?.sms && (
                          <ErrorMessage
                            name={"sms"}
                            component="div"
                            className="error-message"
                          />
                        )}
                      </div>
                    ) : values?.selectedOption === "embedLink" ? (
                      <FormControl
                        fullWidth
                        sx={{ m: 1 }}
                        className="embed-link-input"
                      >
                        <OutlinedInput
                          type={"text"}
                          disabled={false}
                          value={values.embedLink}
                          endAdornment={
                            <InputAdornment position="end">
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    values.embedLink
                                  );
                                  setFieldValue("showCopied", true);
                                  setTimeout(() => {
                                    setFieldValue("showCopied", false);
                                  }, 2000);
                                }}
                              >
                                {values.showCopied ? "copied" : "copy"}
                              </button>
                            </InputAdornment>
                          }
                        />
                      </FormControl>
                    ):  values?.selectedOption === "QR Code" ? (
                      <div className="form-group">
                        <CreateLeadCapturePopupConfirmationQRCode 
                          setLoading={setLoading} 
                          url={qrCodeUrl} 
                        />
                      </div>
                    ) : null}
                  </div>
                  <DialogActions
                    className="confirmation-button-wrapper"
                    style={{ marginTop: "20px" }}
                  >
                    <button
                      onClick={() => {
                        setIsShowConfirmationPopup(false);
                        setCreateLeadCaptureInitialValues(null);
                      }}
                      type="button"
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <div className="save-action-btn">
                      {values?.selectedOption === "QR Code" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                             handlePrint(qrCodeUrl,"Landing Page");
                            }}
                            className="action-btn"
                            style={{ marginRight: "10px" }}
                          >
                            Print
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // Handle download functionality
                              const link = document.createElement('a');
                              link.href = qrCodeUrl;
                              link.download = 'qr-code.png';
                              link.click();
                            }}
                            className="action-btn"
                          >
                            Download
                          </button>
                        </>
                      ) : values?.selectedOption !== "embedLink" ? (
                        <button
                          type={"button"}
                          onClick={() => {
                            validateForm();
                            handleSubmit();
                          }}
                          autoFocus
                          className="action-btn"
                        >
                          Share
                        </button>
                      ) : null}
                    </div>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaveAndSharePopup;
